/**
 * Metronome section of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15 "METRONOME", A§8): clicks on
 * every beat while playing, accented on bar starts from the editor meter. The switch is the editor's metronome
 * state (the same as `playback.metronome`, N), the volume is the user setting `audio.volumes.metronome`.
 */

import type { AppServices } from '../../app/registry/types';
import { Button, Slider, Switch } from '../../ui';
import { m } from './timing.messages';
import { useEditorSelect, useSettingsValue, type TimingText } from './timingHooks';
import s from './Timing.module.css';

export interface MetronomePanelProps {
  readonly services: AppServices;
  readonly text: TimingText;
}

export function MetronomePanel(props: MetronomePanelProps) {
  const { services, text } = props;
  const on = useEditorSelect(services, (st) => st.metronome);
  const settings = useSettingsValue(services);
  const volumes = settings.audio.volumes;
  return (
    <div className={s.stack}>
      <Switch
        label={text.t(m.metronomeOn)}
        checked={on}
        onChange={(v) => {
          services.editor.getState().setMetronome(v);
        }}
      />
      <Slider
        label={text.t(m.metronomeVolume)}
        value={volumes.metronome}
        min={0}
        max={1}
        step={0.05}
        format={(v) => text.t(m.volumePercent, { value: Math.round(v * 100) / 100 })}
        onChange={(v) => {
          services.settings.set('audio', 'volumes', { ...services.settings.value.audio.volumes, metronome: v });
        }}
      />
      <div className={s.row}>
        <Button
          size="sm"
          icon="Volume2"
          onClick={() => {
            void services.audio.ensureContext().then(() => {
              services.audio.preview('metronome');
            });
          }}
        >
          {text.t(m.metronomePreview)}
        </Button>
      </div>
    </div>
  );
}
